import type { ContactSectionContent } from "@site-factory/config";
import { cn } from "../utils";
import { SectionTitle, SectionSubtitle } from "../typography/Typography";
import { Card, CardContent } from "../components/Card";
import { Button } from "../components/Button";
import { SectionShell } from "../components/SectionShell";
import { Icon } from "../icons";

export interface ContactSectionProps extends ContactSectionContent {
  className?: string;
}

export function ContactSection({
  title,
  subtitle,
  contactInfo,
  showForm = true,
  variant = "split",
  className,
}: ContactSectionProps) {
  const isCentered = variant === "centered";

  return (
    <SectionShell className={className} backgroundClassName="bg-surface-alt">
      <div className={cn(isCentered ? "max-w-2xl mx-auto text-center" : "grid md:grid-cols-2 gap-12 items-start")}>
        {/* Info Side */}
        <div className={cn(!isCentered && "space-y-6")}>
          <SectionTitle>{title}</SectionTitle>
          {subtitle && <SectionSubtitle className="mt-4">{subtitle}</SectionSubtitle>}

          {contactInfo && contactInfo.length > 0 && (
            <div className={cn("mt-8 space-y-4", isCentered && "text-left max-w-sm mx-auto")}>
              {contactInfo.map((info, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3"
                  style={{ color: "var(--color-text,#0f172a)" }}
                >
                  <span className="text-xl text-primary">
                    {info.type === "email" && <Icon name="mail" className="h-5 w-5" />}
                    {info.type === "phone" && <Icon name="phone" className="h-5 w-5" />}
                    {info.type === "address" && <Icon name="map-pin" className="h-5 w-5" />}
                  </span>
                  {info.href ? (
                    <a
                      href={info.href}
                      className="font-semibold hover:text-primary transition-colors"
                    >
                      {info.value}
                    </a>
                  ) : (
                    <span style={{ opacity: 0.82 }}>{info.value}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Form Side */}
        {showForm && (
          <Card className="p-6 shadow-sm-2 border border-[color:var(--color-text,#0f172a)]/12 bg-[var(--color-surface,#fff)] text-[var(--color-text,#0f172a)]">
            <CardContent className="p-0">
              <form className="space-y-4">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-semibold mb-1"
                    style={{ color: "var(--color-text,#0f172a)" }}
                  >
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className="w-full px-3 py-2 border border-[color:var(--color-text,#0f172a)]/18 rounded-[12px] focus:outline-none focus:ring-2 focus:ring-primary bg-[var(--color-surface-elevated,var(--color-surface,#fff))] text-[var(--color-text,#0f172a)] placeholder:text-[color:var(--color-text,#0f172a)]/55"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-semibold mb-1"
                    style={{ color: "var(--color-text,#0f172a)" }}
                  >
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="w-full px-3 py-2 border border-[color:var(--color-text,#0f172a)]/18 rounded-[12px] focus:outline-none focus:ring-2 focus:ring-primary bg-[var(--color-surface-elevated,var(--color-surface,#fff))] text-[var(--color-text,#0f172a)] placeholder:text-[color:var(--color-text,#0f172a)]/55"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-semibold mb-1"
                    style={{ color: "var(--color-text,#0f172a)" }}
                  >
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    className="w-full px-3 py-2 border border-[color:var(--color-text,#0f172a)]/18 rounded-[12px] focus:outline-none focus:ring-2 focus:ring-primary resize-none bg-[var(--color-surface-elevated,var(--color-surface,#fff))] text-[var(--color-text,#0f172a)] placeholder:text-[color:var(--color-text,#0f172a)]/55"
                    placeholder="How can we help?"
                  />
                </div>
                <Button type="submit" className="w-full">
                  Send Message
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </SectionShell>
  );
}
